import IDashboard from '../../interfaces/dashboard/IDashboard';
import { useAuthContext } from '../../../auth/contexts/AuthContext';
import useGraphQLClient from '../../../../app/hooks/useGraphQLClient';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useAppSettings } from '../../../../app/features/settings/providers/AppSettingsProvider';
import { useAppNavigationContext } from '../../../../app/features/navigation/providers/AppNavigationProvider';

const useInvestDashboardKpis = () => {
	const auth = useAuthContext();
	const graphQLClient = useGraphQLClient();

	const appSettings = useAppSettings();

	const navigationContext = useAppNavigationContext();

	const { data, isFetching } = useQuery<IDashboard>(
		['investmentDashboardData', appSettings.realizationStatus, navigationContext.currentCurrency],
		async () => {
			const data = (await graphQLClient.request(
				gql`
					query ($isRealized: Boolean, $currency: String) {
						investmentDashboardData(isRealized: $isRealized, currency: $currency) {
							dataUpdatedOn
							isEstimated
							globalKpis {
								globalNetIrr
								globalNetIrrIsNs
								totalValueToPaidIn
								totalValueToPaidInIsNs
								currency
							}
							fundTypes {
								tag
								fundsCount
								calledCapital
								callableCapital
								netCalledCapital
								outstandingCapital
								committedCapital
								grossIrr
								currency
							}
						}
					}
				`,
				{
					isRealized:
						appSettings.realizationStatus === 'all'
							? null
							: appSettings.realizationStatus === 'realized'
							? true
							: false,
					currency: navigationContext.currentCurrency,
				},
			)) as { investmentDashboardData: IDashboard };

			// keep only tag in ['private_credit', 'listed_assets']
			// TODO: wait cleaning in backend
			data.investmentDashboardData.fundTypes = data.investmentDashboardData.fundTypes.filter((fundType) =>
				['private_credit', 'listed_assets'].includes(fundType.tag),
			);
			return data.investmentDashboardData;
		},
		{
			// The query will not execute until the userId exists
			enabled:
				auth.currentCompany !== null &&
				auth.isInvestor &&
				auth.isAuthenticated &&
				navigationContext.currentCurrency !== null,
		},
	);

	return {
		isFetching,
		isEstimated: data?.isEstimated ?? null,
		dataUpdatedOn: data?.dataUpdatedOn ?? null,
		globalKpis: data?.globalKpis ?? null,
		fundTypes: data?.fundTypes ?? null,
	};
};

export default useInvestDashboardKpis;
